import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Pencil, Plus, Trash2 } from 'lucide-react';
import type { CompanyListItem } from '@ip/shared-types';
import { t } from '@/i18n';
import { useCompaniesApi } from '@/services/api';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { CompanyEditModal } from './CompanyEditModal';
import { AddCompanyDialog } from './AddCompanyDialog';
import { DeleteCompanyDialog } from './DeleteCompanyDialog';

const COLUMN_COUNT = 5;

export function CompanyManagement() {
  const { getCompanies } = useCompaniesApi();
  const [companies, setCompanies] = useState<CompanyListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [editingNip, setEditingNip] = useState<string | null>(null);
  const [deletingCompany, setDeletingCompany] = useState<CompanyListItem | null>(null);

  const loadCompanies = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getCompanies();
      setCompanies(data.filter((c) => c.nip !== 'other'));
    } catch (error) {
      toast.error(t('errors.loadingFailed'));
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [getCompanies]);

  useEffect(() => {
    loadCompanies();
  }, [loadCompanies]);

  const editingCompany = companies.find((c) => c.nip === editingNip) ?? null;

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h1 className="text-lg font-semibold tracking-tight">{t('companies.title')}</h1>
          <p className="text-muted-foreground text-sm">{t('companies.subtitle')}</p>
        </div>
        <Button onClick={() => setAddOpen(true)}>
          <Plus className="size-4" />
          {t('companies.addCompany')}
        </Button>
      </div>

      <div className="bg-card rounded-xl border">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead className="text-muted-foreground whitespace-nowrap">
                  {t('companies.columns.name')}
                </TableHead>
                <TableHead className="text-muted-foreground whitespace-nowrap">
                  {t('companies.columns.nip')}
                </TableHead>
                <TableHead className="text-muted-foreground w-[120px] whitespace-nowrap">
                  {t('companies.columns.invoices')}
                </TableHead>
                <TableHead className="text-muted-foreground w-[140px] whitespace-nowrap">
                  {t('companies.columns.contractors')}
                </TableHead>
                <TableHead className="text-muted-foreground w-[100px] whitespace-nowrap">
                  {t('common.actions')}
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                Array.from({ length: 5 }, (_, i) => (
                  <TableRow key={i}>
                    <TableCell colSpan={COLUMN_COUNT}>
                      <Skeleton className="h-8 w-full" />
                    </TableCell>
                  </TableRow>
                ))
              ) : companies.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={COLUMN_COUNT} className="h-40 text-center">
                    <p className="font-medium">{t('companies.noCompanies')}</p>
                  </TableCell>
                </TableRow>
              ) : (
                companies.map((company) => (
                  <TableRow key={company.nip}>
                    <TableCell className="py-2 font-medium">
                      {company.name || company.nip}
                    </TableCell>
                    <TableCell className="py-2 font-mono">{company.nip}</TableCell>
                    <TableCell className="py-2 tabular-nums">{company.invoiceCount}</TableCell>
                    <TableCell className="py-2 tabular-nums">{company.contractorCount}</TableCell>
                    <TableCell className="py-2">
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="icon-sm"
                          onClick={() => setEditingNip(company.nip)}
                          title={t('common.edit')}
                        >
                          <Pencil className="size-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon-sm"
                          className="text-destructive hover:text-destructive"
                          onClick={() => setDeletingCompany(company)}
                          title={t('common.delete')}
                        >
                          <Trash2 className="size-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      <AddCompanyDialog
        open={addOpen}
        onClose={() => setAddOpen(false)}
        onCreated={loadCompanies}
      />

      {editingCompany && (
        <CompanyEditModal
          open
          companyNip={editingCompany.nip}
          companyName={editingCompany.name || editingCompany.nip}
          onClose={() => setEditingNip(null)}
          onSaved={loadCompanies}
        />
      )}

      {deletingCompany && (
        <DeleteCompanyDialog
          open
          nip={deletingCompany.nip}
          companyName={deletingCompany.name || deletingCompany.nip}
          onClose={() => setDeletingCompany(null)}
          onDeleted={loadCompanies}
        />
      )}
    </div>
  );
}
